/**
 * Meta Pixel — thin wrapper around the global `fbq` registered by the
 * inline snippet in index.html.
 *
 * Why a wrapper at all (vs. calling fbq directly from components):
 *   * Type safety — `fbq` is appended to `window` by external markup, so
 *     TypeScript doesn't know it exists. The wrapper centralizes the
 *     `declare global` and the type-narrowing.
 *   * Graceful no-op — on dev preview and localhost the inline snippet
 *     does not initialize fbq (gated on hostname), so calls here just
 *     return without throwing.
 *   * Auditable — every event we send to Meta lives in one file, easy
 *     to grep for and easy to extend (Lead, Purchase, etc. later).
 *
 * Standard event reference (used by Meta's optimization algorithms):
 *   https://developers.facebook.com/docs/meta-pixel/reference#standard-events
 */

declare global {
  interface Window {
    /** Set by the inline Meta Pixel snippet in index.html when the page
     *  loads on app.todowith.ai. Absent on dev/localhost — the wrapper
     *  below tolerates this.
     *
     *  The 4th argument is Meta's options bag — `{ eventID }` is how Meta
     *  deduplicates events across multiple sources (e.g. Pixel + Conversions
     *  API, or the same Pixel firing in two browser contexts because an
     *  email-confirmation link opened in a different app). Two events with
     *  the same eventID arriving at Meta within ~7 days collapse to one. */
    fbq?: (
      command: "track" | "trackCustom" | "init",
      eventName: string,
      params?: Record<string, unknown>,
      options?: { eventID?: string },
    ) => void;
    _fbq?: unknown;
  }
}

/** Options accepted by `trackMetaEvent` / `trackCustomMetaEvent`.
 *
 *  `eventID`: pass a deterministic per-event identifier (e.g. `signup-{user_id}`
 *  for CompleteRegistration) to enable Meta's cross-source / cross-device
 *  deduplication. Same eventID → Meta counts once. */
type MetaEventOptions = { eventID?: string };

/**
 * Fire a Meta Pixel standard event (e.g. "CompleteRegistration", "Lead",
 * "Purchase"). Safe to call from any client component — does nothing if
 * the Pixel wasn't initialized (which is the case off-production).
 *
 * @param eventName  One of Meta's standard event names. Use `trackCustomMetaEvent`
 *                   for non-standard names so Meta's algorithms don't drop them.
 * @param params     Optional event properties (currency, value, content_name…).
 * @param options    Optional Meta options bag — pass `{ eventID }` to enable
 *                   server-side deduplication when the same logical event
 *                   may fire from multiple browser contexts.
 */
export function trackMetaEvent(
  eventName: string,
  params?: Record<string, unknown>,
  options?: MetaEventOptions,
): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") {
    return;
  }
  if (options?.eventID) {
    window.fbq("track", eventName, params ?? {}, { eventID: options.eventID });
  } else if (params) {
    window.fbq("track", eventName, params);
  } else {
    window.fbq("track", eventName);
  }
}

/** Production hostname — the Pixel only loads here. Kept as a constant so
 *  the gate is in one place (this file) and never duplicated to other call
 *  sites that might drift. */
const PROD_HOSTNAME = "app.todowith.ai";
const META_PIXEL_ID = "2869011816776110";

/** Idempotency flag for `loadMetaPixel`: once bootstrapped, repeat calls
 *  do nothing. Lives at module scope on purpose — it persists across React
 *  re-renders but resets on full page reload (which is also when the inline
 *  index.html snippet runs, so the two paths can't collide). */
let pixelBootstrapped = false;

/**
 * Load the Meta Pixel script, init it, and fire a PageView — **only after
 * the user has consented to analytics cookies**.
 *
 * Called in exactly two places:
 *   1. Page load via the inline snippet in `index.html` — for returning
 *      visitors whose consent is already stored in localStorage.
 *   2. `CookieConsentContext` — the moment a first-time visitor flips the
 *      analytics toggle to true. This call path is what makes tracking
 *      work immediately after consent without forcing a page reload.
 *
 * Idempotent: safe to call multiple times; the bootstrap runs once.
 * No-ops on non-production hostnames (dev / preview / localhost).
 */
export function loadMetaPixel(): void {
  if (pixelBootstrapped) return;
  if (typeof window === "undefined") return;
  if (window.location.hostname !== PROD_HOSTNAME) return;

  // Returning user — the inline snippet in index.html already loaded the
  // Pixel. Mark bootstrapped so future calls don't reinitialize.
  if (typeof window.fbq === "function") {
    pixelBootstrapped = true;
    return;
  }

  // First-time consent path — Pixel wasn't loaded at page-load because the
  // user hadn't consented yet. Run the exact same Meta-published snippet
  // here so the Pixel comes online without requiring a page reload.
  // `any` is scoped to this IIFE because the snippet mutates global state
  // in a pattern Meta prescribes but TypeScript can't model precisely.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f = window as any;
  const b = document;
  const e = "script";
  const v = "https://connect.facebook.net/en_US/fbevents.js";
  if (f.fbq) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = (f.fbq = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  });
  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  const t = b.createElement(e) as HTMLScriptElement;
  t.async = true;
  t.src = v;
  const s = b.getElementsByTagName(e)[0];
  s.parentNode?.insertBefore(t, s);

  // Call through `f` (the `any`-typed window alias from above) — calling via
  // `window.fbq?.(...)` would trip TypeScript's narrowing from the early
  // `typeof window.fbq === "function" return` guard, even though we just
  // assigned `f.fbq = n` above.
  f.fbq("init", META_PIXEL_ID);
  f.fbq("track", "PageView");
  pixelBootstrapped = true;
}

/** Like `trackMetaEvent` but uses the `trackCustom` command — required for
 *  any event name that isn't one of Meta's standard events. */
export function trackCustomMetaEvent(
  eventName: string,
  params?: Record<string, unknown>,
  options?: MetaEventOptions,
): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") {
    return;
  }
  if (options?.eventID) {
    window.fbq("trackCustom", eventName, params ?? {}, {
      eventID: options.eventID,
    });
  } else if (params) {
    window.fbq("trackCustom", eventName, params);
  } else {
    window.fbq("trackCustom", eventName);
  }
}
